import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  registerLoadingFunctions,
  unregisterLoadingFunctions,
} from "./loading-bridge";

/* ──────────────────────────────────────────────
 *  Types
 * ────────────────────────────────────────────── */

interface LoadingContextType {
  /** Whether the loader is currently visible. */
  isLoading: boolean;
  /** Increment the reference counter and schedule the loader to appear. */
  showLoader: () => void;
  /** Decrement the reference counter and schedule the loader to hide when it reaches 0. */
  hideLoader: () => void;
}

/* ──────────────────────────────────────────────
 *  Constants
 * ────────────────────────────────────────────── */

/** Don't show the loader for operations shorter than this. */
const SHOW_DELAY_MS = 0;

/** Once visible, keep the loader on screen for at least this long. */
const MIN_VISIBLE_MS = 1000;

/* ──────────────────────────────────────────────
 *  Context
 * ────────────────────────────────────────────── */

const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  showLoader: () => {},
  hideLoader: () => {},
});

/**
 * Hook to consume the loading context.
 *
 * @example
 * ```tsx
 * const { isLoading, showLoader, hideLoader } = useLoading();
 *
 * const handleSubmit = async () => {
 *   showLoader();
 *   try { await submitForm(); }
 *   finally { hideLoader(); }
 * };
 * ```
 */
export const useLoading = (): LoadingContextType => useContext(LoadingContext);

/* ──────────────────────────────────────────────
 *  Provider
 * ────────────────────────────────────────────── */

export const LoadingProvider: React.FC = ({ children }) => {
  const [visible, setVisible] = useState(false);

  // ── refs (stable across renders, no stale closures) ──
  const refCountRef = useRef(0);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleSinceRef = useRef<number | null>(null);
  const isVisibleRef = useRef(false);

  /* ·························
   *  showLoader
   * ························· */
  const showLoader = useCallback(() => {
    refCountRef.current += 1;

    // Cancel any pending hide — a new operation just started
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Already visible → nothing to do
    if (isVisibleRef.current) return;

    // Show immediately
    isVisibleRef.current = true;
    visibleSinceRef.current = Date.now();
    setVisible(true);
  }, []);

  /* ·························
   *  hideLoader
   * ························· */
  const hideLoader = useCallback(() => {
    refCountRef.current = Math.max(0, refCountRef.current - 1);

    // Other operations still in flight
    if (refCountRef.current > 0) return;

    // Not visible → nothing to hide
    if (!isVisibleRef.current) return;

    // Ensure minimum on-screen time to avoid flicker
    const elapsed = visibleSinceRef.current
      ? Date.now() - visibleSinceRef.current
      : 0;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      isVisibleRef.current = false;
      visibleSinceRef.current = null;
      setVisible(false);
    }, remaining);
  }, []);

  /* ·························
   *  Bridge registration
   * ························· */
  useEffect(() => {
    registerLoadingFunctions(showLoader, hideLoader);
    return () => {
      unregisterLoadingFunctions();
    };
  }, [showLoader, hideLoader]);

  /* ·························
   *  Timer cleanup on unmount
   * ························· */
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <LoadingContext.Provider
      value={{ isLoading: visible, showLoader, hideLoader }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
